import React, {
  useCallback, useEffect, useState, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Link,
  Redirect,
  useParams,
  useLocation,
  useHistory,
} from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Button, Container } from 'reactstrap';

import {
  APIgetPage,
  APIgetPageCollectionDetail,
  APIgetPageCollectionFiles,
} from '../api/Pages';
import { isLoggedIn } from '../api/Auth';
import { cleanLoginParams } from '../helpers/App';
import { getSetting } from '../helpers/Settings';
import { showModal } from '../actions';
import { thumbnailPlaceholder } from '../helpers/Collections';
import PageHeader from '../components/Page/Header';
import PageFooter from '../components/Page/Footer';
import Loading from '../components/Loading';
import { PageIdContext } from '../helpers/PageIdContext';
import { getPageFileRoute, getTransparentHeaderPaddingClass } from '../helpers/PageSections';
import FilesMasonry from '../components/Files/Masonry';
import FilesGrid from '../components/Files/Grid';
import FilesTile from '../components/Files/Tile';
import CollectionsGrid from '../components/Collections/Grid';
import { renderPageCollectionAction } from '../helpers/PageElementActions';
import Actionbar from '../components/Actionbar';

const VALID_VIEWS = ['grid', 'framed', 'masonry', 'tile', 'list'];
const FILES_PER_PAGE = 36;
const COLLECTIONS_PER_PAGE = 24;
const pageCollectionViewCache = new Map();

const PageCollectionView = ({ onShowModal, fixedModal }) => {
  const intl = useIntl();
  const { pageId, collectionUuid } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { pathname, search, state: locationState } = location;
  const safeCollectionUuid = (collectionUuid || '').split('?')[0];
  const pageBasePath = pathname.startsWith('/pages/') ? 'pages' : 'content';
  const [page, setPage] = useState(null);
  const [collectionMeta, setCollectionMeta] = useState(null);
  const [childCollections, setChildCollections] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filesPage, setFilesPage] = useState(1);
  const [hasMoreFiles, setHasMoreFiles] = useState(false);
  const [isFetchingMoreFiles, setIsFetchingMoreFiles] = useState(false);
  const [collectionsPage, setCollectionsPage] = useState(1);
  const [hasMoreCollections, setHasMoreCollections] = useState(false);
  const [isFetchingMoreCollections, setIsFetchingMoreCollections] = useState(false);
  const [redirectToLogin, setRedirectToLogin] = useState(false);
  const modalScrollRef = useRef(null);
  const isModalView = !!fixedModal;
  const cacheKey = useMemo(
    () => `${pageBasePath}:${pageId}:${safeCollectionUuid}`,
    [pageBasePath, pageId, safeCollectionUuid],
  );

  useEffect(() => {
    if (!isModalView || typeof document === 'undefined') {
      return undefined;
    }

    const className = 'page-collection-modal-open';
    document.documentElement.classList.add(className);
    document.body.classList.add(className);

    return () => {
      document.documentElement.classList.remove(className);
      document.body.classList.remove(className);
    };
  }, [isModalView]);

  useEffect(() => {
    let cancelled = false;
    const cachedState = pageCollectionViewCache.get(cacheKey);

    if (cachedState) {
      setPage(cachedState.page || null);
      setCollectionMeta(cachedState.collectionMeta || null);
      setChildCollections(cachedState.childCollections || []);
      setFiles(cachedState.files || []);
      setFilesPage(cachedState.filesPage || 1);
      setCollectionsPage(cachedState.collectionsPage || 1);
      setHasMoreFiles(!!cachedState.hasMoreFiles);
      setHasMoreCollections(!!cachedState.hasMoreCollections);
      setError(null);
      setRedirectToLogin(false);
      setIsFetchingMoreFiles(false);
      setIsFetchingMoreCollections(false);
      setLoading(false);
    } else {
      setLoading(true);
      setError(null);
      setRedirectToLogin(false);
      setFilesPage(1);
      setCollectionsPage(1);
      setHasMoreFiles(false);
      setHasMoreCollections(false);
      setIsFetchingMoreFiles(false);
      setIsFetchingMoreCollections(false);
    }

    Promise.all([
      APIgetPage(pageId),
      APIgetPageCollectionDetail(pageId, safeCollectionUuid, {
        page: 1,
        per_page: COLLECTIONS_PER_PAGE,
      }),
      APIgetPageCollectionFiles(pageId, safeCollectionUuid, { page: 1, per_page: FILES_PER_PAGE }),
    ])
      .then(([pageRes, colDetailRes, colResFiles]) => {
        if (cancelled) return;
        const nextPage = pageRes.data;
        const nextCollectionMeta = colDetailRes.data?.collection ?? null;
        const nextChildCollections = colDetailRes.data?.child_collections ?? [];
        const nextFiles = colResFiles.data?.data?.data ?? colResFiles.data?.data ?? [];
        const nextHasMoreFiles = !!(
          colResFiles.data?.data?.meta?.has_more
          ?? colResFiles.data?.meta?.has_more
        );
        const nextHasMoreCollections = !!colDetailRes.data?.meta?.has_more;

        setPage(nextPage);
        setCollectionMeta(nextCollectionMeta);
        setChildCollections(nextChildCollections);
        setFiles(nextFiles);
        setFilesPage(1);
        setCollectionsPage(1);
        setHasMoreFiles(nextHasMoreFiles);
        setHasMoreCollections(nextHasMoreCollections);
        setLoading(false);

        pageCollectionViewCache.set(cacheKey, {
          page: nextPage,
          collectionMeta: nextCollectionMeta,
          childCollections: nextChildCollections,
          files: nextFiles,
          filesPage: 1,
          collectionsPage: 1,
          hasMoreFiles: nextHasMoreFiles,
          hasMoreCollections: nextHasMoreCollections,
        });
      })
      .catch((requestError) => {
        if (!cancelled) {
          if (requestError.response?.status === 403 && requestError.response?.data?.error === 'login_required') {
            setRedirectToLogin(true);
            setLoading(false);
            return;
          }

          setError(intl.formatMessage({ defaultMessage: 'Failed to load collection.' }));
          if (!cachedState) {
            setLoading(false);
          }
        }
      });

    return () => { cancelled = true; };
  }, [cacheKey, pageId, safeCollectionUuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const isUserLoggedIn = isLoggedIn();
  const cs1Class = getSetting('cs1') ? ' cs1' : '';
  const headerSearchClass = getTransparentHeaderPaddingClass(
    page?.options?.header,
    false,
    page?.options?.showSearchBar ?? page?.options?.show_search_bar ?? true,
  );

  const selectedView = useMemo(() => {
    const requestedView = new URLSearchParams(search).get('view');
    return VALID_VIEWS.includes(requestedView) ? requestedView : 'grid';
  }, [search]);
  const resolvedPageId = page?.id || pageId;
  const resolvedPageRouteId = page?.url || pageId;
  const canDownloadCollection = !!(collectionMeta?.allow_download);
  const hasCollectionData = !!collectionMeta || childCollections.length > 0 || files.length > 0;
  const defaultBackPath = `/${pageBasePath}/${resolvedPageRouteId}`;
  const currentUrl = `${pathname}${search || ''}`;
  const incomingBackTrail = useMemo(() => (
    Array.isArray(locationState?.backTrail)
      ? locationState.backTrail.filter(Boolean)
      : []
  ), [locationState]);
  const backLinkTarget = incomingBackTrail.length > 0
    ? incomingBackTrail[incomingBackTrail.length - 1]
    : defaultBackPath;

  const getLocationFromUrl = useCallback((url, state = undefined) => {
    const [targetPathname, targetSearch = ''] = String(url || '').split('?');
    return {
      pathname: targetPathname,
      search: targetSearch ? `?${targetSearch}` : '',
      state,
    };
  }, []);

  const handleGoBack = useCallback((event) => {
    if (event) {
      event.preventDefault();
    }

    if (incomingBackTrail.length > 0) {
      const target = incomingBackTrail[incomingBackTrail.length - 1];
      const nextTrail = incomingBackTrail.slice(0, -1);
      history.replace(getLocationFromUrl(target, {
        backTrail: nextTrail,
      }));
      return;
    }

    // Default return is page when no back trail is provided
    history.replace(defaultBackPath);
  }, [
    defaultBackPath,
    getLocationFromUrl,
    history,
    incomingBackTrail,
  ]);

  const handleDownloadCollection = () => {
    if (!collectionMeta) {
      return;
    }

    onShowModal('DownloadModal', {
      className: 'modal-download',
      files: [],
      collections: [collectionMeta],
      pageId: resolvedPageId,
      backdrop: true,
    });
  };

  const renderDisplayAction = useCallback((targetFile, actionView) => {
    if (!canDownloadCollection || !resolvedPageId || !targetFile?.uuid) {
      return null;
    }

    const label = intl.formatMessage({ defaultMessage: 'Download' });
    const actionId = `PageCollectionViewDownload-${targetFile.uuid}-${actionView || selectedView || 'grid'}`;

    return (
      <li>
        <a
          href="#download"
          id={actionId}
          aria-label={label}
          title={label}
          onClick={(e) => {
            e.preventDefault();
            onShowModal('DownloadModal', {
              className: 'modal-download',
              files: [targetFile],
              collections: [],
              fileCollectionMap: safeCollectionUuid ? {
                [targetFile.uuid]: [safeCollectionUuid],
              } : {},
              pageId: resolvedPageId,
              backdrop: true,
            });
          }}
        >
          <i className="far fa-download" aria-hidden="true" />
        </a>
      </li>
    );
  }, [canDownloadCollection, intl, onShowModal, resolvedPageId, selectedView, safeCollectionUuid]);

  const renderChildCollectionDisplayAction = useCallback(targetCollection => (
    renderPageCollectionAction({
      collection: targetCollection,
      pageId: resolvedPageId,
      onShowModal,
      intl,
    })
  ), [intl, onShowModal, resolvedPageId]);

  const header = (
    <PageHeader
      headerType={page?.options?.header}
      pageLogoUrl={page?.options?.logo_url_preview}
      showSearchBar={page?.options?.showSearchBar ?? page?.options?.show_search_bar ?? true}
      pageNavigation={page?.options?.pageNavigation || page?.options?.page_navigation || []}
      pageBasePath={pageBasePath}
      pageRouteId={pageId}
      pageId={page?.id || pageId}
      pageVisibilityMode={page?.visibility_mode || page?.options?.visibility_mode || null}
    />
  );

  const fetchMoreFiles = useCallback(() => {
    if (!hasMoreFiles || isFetchingMoreFiles) return;

    setIsFetchingMoreFiles(true);
    APIgetPageCollectionFiles(
      pageId,
      safeCollectionUuid,
      { page: filesPage + 1, per_page: FILES_PER_PAGE },
    )
      .then((res) => {
        const newFiles = res.data?.data?.data ?? res.data?.data ?? [];
        const nextHasMoreFiles = !!(
          res.data?.data?.meta?.has_more
          ?? res.data?.meta?.has_more
        );

        setFiles((prev) => {
          const merged = [...prev, ...newFiles];
          const existing = pageCollectionViewCache.get(cacheKey) || {};
          pageCollectionViewCache.set(cacheKey, {
            ...existing,
            files: merged,
            filesPage: filesPage + 1,
            hasMoreFiles: nextHasMoreFiles,
          });
          return merged;
        });
        setFilesPage((prev) => prev + 1);
        setHasMoreFiles(nextHasMoreFiles);
        setIsFetchingMoreFiles(false);
      })
      .catch(() => setIsFetchingMoreFiles(false));
  }, [
    hasMoreFiles,
    isFetchingMoreFiles,
    pageId,
    safeCollectionUuid,
    filesPage,
    cacheKey,
  ]);

  const fetchMoreCollections = useCallback(() => {
    if (!hasMoreCollections || isFetchingMoreCollections) return;

    setIsFetchingMoreCollections(true);
    APIgetPageCollectionDetail(
      pageId,
      safeCollectionUuid,
      { page: collectionsPage + 1, per_page: COLLECTIONS_PER_PAGE },
    )
      .then((res) => {
        const newCollections = res.data?.child_collections ?? [];
        const nextHasMoreCollections = !!res.data?.meta?.has_more;

        setChildCollections((prev) => {
          const merged = [...prev, ...newCollections];
          const existing = pageCollectionViewCache.get(cacheKey) || {};
          pageCollectionViewCache.set(cacheKey, {
            ...existing,
            childCollections: merged,
            collectionsPage: collectionsPage + 1,
            hasMoreCollections: nextHasMoreCollections,
          });
          return merged;
        });
        setCollectionsPage((prev) => prev + 1);
        setHasMoreCollections(nextHasMoreCollections);
        setIsFetchingMoreCollections(false);
      })
      .catch(() => setIsFetchingMoreCollections(false));
  }, [
    hasMoreCollections,
    isFetchingMoreCollections,
    pageId,
    safeCollectionUuid,
    collectionsPage,
    cacheKey,
  ]);

  useEffect(() => {
    if (isModalView) {
      const scrollEl = modalScrollRef.current;
      if (!scrollEl) {
        return undefined;
      }

      const handleModalScroll = () => {
        if (scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 100) {
          fetchMoreFiles();
          fetchMoreCollections();
        }
      };

      scrollEl.addEventListener('scroll', handleModalScroll);
      return () => scrollEl.removeEventListener('scroll', handleModalScroll);
    }

    const handleWindowScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100
      ) {
        fetchMoreFiles();
        fetchMoreCollections();
      }
    };

    window.addEventListener('scroll', handleWindowScroll);
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [fetchMoreCollections, fetchMoreFiles, isModalView]);

  const pageContextValue = useMemo(
    () => ({
      pageId: resolvedPageId,
      pageRouteId: resolvedPageRouteId,
      isEditing: false,
      pageBasePath,
      visibilityMode: page?.visibility_mode || page?.options?.visibility_mode || null,
    }),
    [resolvedPageId, resolvedPageRouteId, pageBasePath, page],
  );

  const renderChildCollections = () => {
    if (!Array.isArray(childCollections) || childCollections.length === 0) {
      return null;
    }

    return (
      <div className="widget-files-big-list puck-feed-grid-wrapper puck-collection-view-grid-wrapper">
        <h3 className="puck-section-subtitle puck-collection-view-section-name">
          {intl.formatMessage({ defaultMessage: 'Collections' })}
        </h3>
        <CollectionsGrid
          collections={childCollections}
          canDrag={false}
          allowCheck={false}
          displayOnly={true}
          renderDisplayAction={renderChildCollectionDisplayAction}
          className="puck-page-collection-children-grid"
          collectionUrlResolver={collection => ({
            pathname: `/${pageBasePath}/${resolvedPageRouteId}/collection/${collection.uuid}`,
            state: {
              backTrail: incomingBackTrail[incomingBackTrail.length - 1] === currentUrl
                ? incomingBackTrail
                : [...incomingBackTrail, currentUrl],
            },
          })}
        />
      </div>
    );
  };

  if (redirectToLogin) {
    return (
      <Redirect
        to={{
          pathname: '/login',
          search,
          state: {
            from: `${pathname}${cleanLoginParams(search)}`,
          },
        }}
      />
    );
  }

  if (loading) {
    if (isModalView) {
      return (
        <div className="page-collection-view-modal landing-content">
          <div ref={modalScrollRef} className="page-collection-view-modal-scroll">
            <Container className="puck-collection-view mt-4 mb-4">
              <Loading />
            </Container>
          </div>
        </div>
      );
    }

    return (
      <div className={`layout layout-landing${cs1Class}`}>
        <div className={`landing-content${cs1Class} page-header-${page?.options?.header}${headerSearchClass}`}><Loading /></div>
        <PageFooter />
      </div>
    );
  }

  if (error || !hasCollectionData) {
    if (isModalView) {
      return (
        <PageIdContext.Provider value={pageContextValue}>
          <div className="page-collection-view-modal landing-content">
            <div ref={modalScrollRef} className="page-collection-view-modal-scroll">
              {header}
              <Container className="puck-collection-view mt-4 mb-4">
                <p className="mt-4">{error || intl.formatMessage({ defaultMessage: 'Collection not found.' })}</p>
                <a href={backLinkTarget} onClick={handleGoBack} className="puck-collection-view-back">
                  <i className="far fa-arrow-left" />
                  {intl.formatMessage({ defaultMessage: 'Back' })}
                </a>
              </Container>
            </div>
          </div>
        </PageIdContext.Provider>
      );
    }

    return (
      <div className={`layout layout-landing${cs1Class}`}>
        {isUserLoggedIn && header}
        <div className={`landing-content${cs1Class} page-header-${page?.options?.header}${headerSearchClass}`}>
          <Container>
            <p className="mt-4">{error || intl.formatMessage({ defaultMessage: 'Collection not found.' })}</p>
            <a href={backLinkTarget} onClick={handleGoBack}>
              <i className="far fa-arrow-left" />
              {intl.formatMessage({ defaultMessage: 'Back' })}
            </a>
          </Container>
        </div>
        <PageFooter />
        <Actionbar />
      </div>
    );
  }

  const visibleFiles = Array.isArray(files)
    ? files
      .filter(file => file.urls)
      .map(file => ({
        ...file,
        to: (() => {
          const route = getPageFileRoute({
            pageBasePath,
            pageRouteId: resolvedPageRouteId,
            pageId: resolvedPageId,
            file,
          });

          if (!route) {
            return null;
          }

          return {
            ...route,
            state: {
              ...(route.state || {}),
              backUrl: `${pathname}${search}`,
              backUrlState: {
                backTrail: incomingBackTrail,
              },
              pageCollectionAllowDownload: canDownloadCollection,
              pageCollectionUuid: safeCollectionUuid || null,
            },
          };
        })(),
      }))
    : [];

  const renderFiles = () => {
    if (selectedView === 'grid') {
      return (
        <div className="widget-files-big-list puck-feed-grid-wrapper puck-collection-view-grid-wrapper">
          <h3 className="puck-section-subtitle puck-collection-view-section-name">
            {intl.formatMessage({ defaultMessage: 'Files' })}
          </h3>
          <FilesGrid
            files={visibleFiles}
            canDrag={false}
            allowCheck={true}
            displayOnly={true}
            renderDisplayAction={renderDisplayAction}
            className="puck-feed-system-grid"
          />
        </div>
      );
    }

    if (selectedView === 'masonry') {
      return (
        <FilesMasonry
          files={visibleFiles}
          displayOnly={true}
          allowCheck={true}
          renderDisplayAction={renderDisplayAction}
        />
      );
    }

    if (selectedView === 'tile') {
      return (
        <FilesTile
          files={visibleFiles}
          displayOnly={true}
          allowCheck={true}
          disableRowConstraints={true}
          renderDisplayAction={renderDisplayAction}
        />
      );
    }

    return (
      <div className="puck-feed-list">
        {visibleFiles.map((file) => (
          <Link key={file.uuid} to={file.to || `/files/${file.uuid}`} className="puck-feed-file-link">
            <div className="puck-feed-list-item">
              <img
                src={file.urls?.thumbnailsq || file.urls?.thumbnail || thumbnailPlaceholder}
                alt={file.name || ''}
                className="puck-feed-list-thumb"
              />
              <span className="puck-feed-list-name">{file.name || ''}</span>
              {canDownloadCollection && (
                <Button
                  color="link"
                  size="sm"
                  className="puck-feed-list-download"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onShowModal('DownloadModal', {
                      className: 'modal-download',
                      files: [file],
                      collections: [],
                      fileCollectionMap: safeCollectionUuid ? {
                        [file.uuid]: [safeCollectionUuid],
                      } : {},
                      pageId: resolvedPageId,
                      backdrop: true,
                    });
                  }}
                >
                  <i className="far fa-download" aria-hidden="true" />
                </Button>
              )}
            </div>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <PageIdContext.Provider value={pageContextValue}>
      {isModalView ? (
        <div className="page-collection-view-modal landing-content">
          <div ref={modalScrollRef} className="page-collection-view-modal-scroll">
            {header}
            <Container className="puck-collection-view mt-4 mb-4">
              <div className="puck-collection-view-header">
                <a href={backLinkTarget} onClick={handleGoBack} className="puck-collection-view-back">
                  <i className="far fa-arrow-left" aria-hidden="true" />
                  <span>{intl.formatMessage({ defaultMessage: 'Back' })}</span>
                </a>
                <div className="puck-collection-view-header-main">
                  <h1 className="puck-section-title">
                    <span>{collectionMeta?.name || intl.formatMessage({ defaultMessage: 'Unnamed Collection' })}</span>
                  </h1>
                  {canDownloadCollection && (
                    <Button
                      color="primary"
                      className="puck-collection-view-download"
                      onClick={handleDownloadCollection}
                    >
                      <i className="far fa-download" /> {intl.formatMessage({ defaultMessage: 'Download all' })}
                    </Button>
                  )}
                </div>
              </div>
              {renderChildCollections()}
              {visibleFiles.length > 0 && renderFiles()}
              {(isFetchingMoreFiles || isFetchingMoreCollections) && <Loading />}
            </Container>
          </div>
        </div>
      ) : (
        <div className={`layout layout-landing${cs1Class}`}>
          {header}
          <div className={`landing-content${cs1Class} page-header-${page?.options?.header}${headerSearchClass}`}>
            <Container className="puck-collection-view mt-4 mb-4">
              <div className="puck-collection-view-header">
                <a href={backLinkTarget} onClick={handleGoBack} className="puck-collection-view-back">
                  <i className="far fa-arrow-left" aria-hidden="true" />
                  <span>{intl.formatMessage({ defaultMessage: 'Back' })}</span>
                </a>
                <div className="puck-collection-view-header-main">
                  <h1 className="puck-section-title">
                    <span>{collectionMeta?.name || intl.formatMessage({ defaultMessage: 'Unnamed Collection' })}</span>
                  </h1>
                  {canDownloadCollection && (
                    <Button
                      color="primary"
                      className="puck-collection-view-download"
                      onClick={handleDownloadCollection}
                    >
                      <i className="far fa-download" /> {intl.formatMessage({ defaultMessage: 'Download all' })}
                    </Button>
                  )}
                </div>
              </div>
              {renderChildCollections()}
              {visibleFiles.length > 0 && renderFiles()}
              {(isFetchingMoreFiles || isFetchingMoreCollections) && <Loading />}
            </Container>
          </div>
          <PageFooter />
        </div>
      )}
    </PageIdContext.Provider>
  );
};

PageCollectionView.propTypes = {
  onShowModal: PropTypes.func.isRequired,
  fixedModal: PropTypes.bool,
};

PageCollectionView.defaultProps = {
  fixedModal: false,
};

const mapDispatchToProps = (dispatch) => ({
  onShowModal: (modalName, params) => dispatch(showModal(modalName, params)),
});

export default connect(null, mapDispatchToProps)(PageCollectionView);
